import React from 'react';
import styled from 'styled-components';
import { EmployeeRecord } from '../../types/drilldown';
import { Colors, Spacing, FontSizes, BorderRadius, FontWeights } from '../../constants/theme';
import ModernTable, { Column, CellHelpers } from '../tables/ModernTable';

interface DrillDownIndividualsTableProps {
  individuals: EmployeeRecord[];
  loading?: boolean;
}

const TableContainer = styled.div`
  margin: ${Spacing.xl}px 0;
  border-radius: ${BorderRadius.md}px;
  border: 1px solid ${Colors.border};
  background: ${Colors.surface};
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${Spacing.lg}px ${Spacing.xl}px;
  background: ${Colors.backgroundLight};
  border-bottom: 1px solid ${Colors.border};
`;

const TableTitle = styled.h3`
  font-size: ${FontSizes.lg}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  margin: 0;
`;

const RecordCount = styled.span`
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textTertiary};
`;

const EmptyState = styled.div`
  padding: ${Spacing.xxxl}px;
  text-align: center;
  color: ${Colors.textTertiary};
`;

const LoadingSkeleton = styled.div`
  height: 300px;
  background: ${Colors.backgroundTertiary};
  border-radius: ${BorderRadius.md}px;
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
  }
`;

export const DrillDownIndividualsTable: React.FC<DrillDownIndividualsTableProps> = ({
  individuals,
  loading = false,
}) => {
  if (loading) {
    return <LoadingSkeleton />;
  }

  if (!individuals || individuals.length === 0) {
    return (
      <TableContainer>
        <TableHeader>
          <TableTitle>Individual Records</TableTitle>
        </TableHeader>
        <EmptyState>
          No individual records available at this level.
        </EmptyState>
      </TableContainer>
    );
  }

  const columns: Column[] = [
    {
      key: 'employee',
      header: 'Employee',
      sortable: true,
      render: (_, row: EmployeeRecord) => (
        <div>
          <CellHelpers.Avatar name={row.displayName || `Employee ${row.id}`} />
          {row.jobTitle && (
            <div style={{ 
              fontSize: FontSizes.xs, 
              color: Colors.textTertiary, 
              marginTop: Spacing.xs,
              marginLeft: 44 // Offset for avatar
            }}>
              {row.jobTitle}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'department',
      header: 'Department',
      sortable: true,
    },
    {
      key: 'jobLevel',
      header: 'Level',
      sortable: true,
      render: (value) => <CellHelpers.Badge value={value} variant="info" />
    },
    {
      key: 'country',
      header: 'Location',
      sortable: true,
    },
    {
      key: 'tenure',
      header: 'Tenure',
      sortable: true,
      align: 'center',
      render: (value) => `${value} years`
    },
    {
      key: 'engagementScore',
      header: 'Engagement',
      sortable: true,
      align: 'center',
      render: (value) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <CellHelpers.Progress 
            value={(value || 0) * 10} 
            color={value >= 8 ? Colors.success : value >= 6 ? Colors.warning : Colors.danger}
            label={`${(value || 0).toFixed(1)}/10`}
          />
        </div>
      )
    },
    {
      key: 'attritionRisk',
      header: 'Attrition Risk',
      sortable: true,
      align: 'center',
      render: (value) => {
        const riskLevel = value || 'low';
        const variant = riskLevel === 'high' ? 'danger' : 
                       riskLevel === 'medium' ? 'warning' : 'success';
        return <CellHelpers.Badge value={riskLevel.toUpperCase()} variant={variant} />;
      }
    },
    {
      key: 'status',
      header: 'Status',
      align: 'center',
      render: (_, row: EmployeeRecord) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: Spacing.xs }}>
          {row.isSuccessor && (
            <CellHelpers.Badge value="Successor" variant="info" />
          )}
          {row.performanceRating && (
            <div style={{ 
              fontSize: FontSizes.xs, 
              color: Colors.textTertiary,
              textAlign: 'center'
            }}>
              Performance: {row.performanceRating}
            </div>
          )}
        </div>
      )
    }
  ];

  // Add id field for the table
  const tableData = individuals.map(individual => ({
    ...individual,
    id: individual.id || Math.random().toString()
  }));

  return (
    <TableContainer>
      <TableHeader>
        <TableTitle>Individual Records</TableTitle>
        <RecordCount>{individuals.length} employees</RecordCount>
      </TableHeader>

      <div style={{ padding: Spacing.md }}>
        <ModernTable
          columns={columns}
          data={tableData}
          keyField="id"
          showPagination={individuals.length > 10}
          pageSize={10}
          currentPage={1}
          onPageChange={(page) => console.log('Page:', page)}
          onRowClick={(row) => console.log('Employee clicked:', row)}
          emptyMessage="No employee records available"
        />
      </div>
    </TableContainer>
  );
};